import { Observable, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';

import { Injectable } from '@angular/core';

import { assertArray } from '@app/core/utilities/array.utilities';
import { ApiEndpoint, UserApiRoute } from '../models/api.models';
import { User, UserSetting, UserSettings } from '../models/user.models';
import { ApiInstanceService } from './api-instance.service';
import { ApiService } from './api.service';

@Injectable({
  providedIn: 'root',
})
export class UserApiService extends ApiInstanceService {
  readonly endpoint = ApiEndpoint.Users;

  constructor(api: ApiService) {
    super(api);
  }

  getUserById(userId: number): Observable<User | undefined> {
    return this.getObject<User>({ route: [userId] }).pipe(
      map((response) => response.data),
      catchError(() => of(undefined)),
    );
  }

  updateUser(userId: number, update: Partial<User>): Observable<User | undefined> {
    return this.patch<User>({ route: [userId], data: update }).pipe(
      map((response) => response.data),
      catchError(() => of(undefined)),
    );
  }

  getUserSettings(): Observable<UserSettings> {
    return this.getAll<{ settingKey: UserSetting; settingValue: any }>({
      route: UserApiRoute.ApolloSettings,
    }).pipe(
      catchError(() => of([])),
      map((settings) =>
        assertArray(settings).reduce(
          (cumm, setting) => ({ ...cumm, [setting.settingKey]: setting.settingValue }),
          {} as UserSettings,
        ),
      ),
    );
  }

  changeUserPassword(password: string): Observable<unknown> {
    return this.patch({
      route: '0',
      data: { password },
    });
  }

  putUserSettings(settings: Partial<UserSettings>): Observable<unknown> {
    const data = Object.entries(settings).map(([settingKey, settingValue]) => ({
      setting_key: settingKey,
      setting_value: settingValue,
    }));

    return this.put({ route: UserApiRoute.ApolloSettings, data });
  }

  resendEmailVerification(): Observable<unknown> {
    return this.post({ route: [0, UserApiRoute.EmailVerification] });
  }
}
